import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { refreshSubscriptionStatus, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const initializeSubscription = async () => {
      try {
        // Refresh subscription status after successful payment
        await refreshSubscriptionStatus();
      } catch (error) {
        console.error('Error refreshing subscription status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Add a small delay to ensure Stripe webhook has processed
    const timer = setTimeout(initializeSubscription, 2000);
    
    return () => clearTimeout(timer);
  }, [refreshSubscriptionStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-3xl shadow-2xl border border-white/30 dark:border-gray-700/30 p-8 text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          {user?.subscriptionStatus === 'trialing' 
            ? '🎉 Your Free Trial Has Started!'
            : '🎉 Welcome to Svarra Complete!'
          }
        </h1>

        <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
          {user?.subscriptionStatus === 'trialing'
            ? `Great! You have ${user.trialDaysRemaining || 14} days to explore all Svarra Complete features for free.`
            : 'Your subscription has been activated successfully. You now have access to all premium features!'
          }
        </p>

        {/* Subscription Details */}
        {user?.subscriptionStatus === 'trialing' ? (
          // Trial Details
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4 mb-6">
            <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">Trial Details</h3>
            <div className="text-sm text-green-700 dark:text-green-300">
              <p>Plan: <span className="font-medium">Svarra Complete (Free Trial)</span></p>
              <p>Trial Length: <span className="font-medium">14 days</span></p>
              <p>After Trial: <span className="font-medium">$250/month</span></p>
              <p>Status: <span className="font-medium text-green-600 dark:text-green-400">
                {isLoading ? 'Activating...' : 'Trial Active'}
              </span></p>
              {user?.trialDaysRemaining && (
                <p className="mt-2 font-medium">
                  Days Remaining: <span className="text-green-600 dark:text-green-400">{user.trialDaysRemaining}</span>
                </p>
              )}
            </div>
          </div>
        ) : (
          // Direct Subscription Details
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4 mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Subscription Details</h3>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p>Plan: <span className="font-medium">Svarra Complete</span></p>
              <p>Price: <span className="font-medium">$250/month</span></p>
              <p>Status: <span className="font-medium text-green-600 dark:text-green-400">
                {isLoading ? 'Activating...' : (user?.hasActiveSubscription ? 'Active' : 'Processing...')}
              </span></p>
            </div>
          </div>
        )}

        {/* Features List */}
        <div className="text-left mb-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-center">What's Included:</h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Up to 5 AI agents
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Unlimited conversations
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Appointment booking & calendar sync
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Analytics & reporting
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Link
            to="/dashboard"
            className="w-full block bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white py-3 px-6 rounded-2xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105"
          >
Start Building Your AI Agent
          </Link>
          
          <Link
            to="/settings?tab=billing"
            className="w-full block bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 px-6 rounded-2xl font-medium transition-all duration-300"
          >
            {user?.subscriptionStatus === 'trialing' ? 'Manage Trial' : 'Manage Subscription'}
          </Link>
        </div>

        {/* Session Info (for debugging) */}
        {sessionId && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Session ID: {sessionId.substring(0, 20)}...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;